import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, EntityKeyByReference, PaginatedApiResponse } from '@card-software/library';
import { DispatchRequest } from '../../models/dispatch-request/dispatch-request';
import { DispatchRequestSearchCommand } from '../../models/inventory/dispatch-request-search';
import { getSkipperErrorHeader } from '../../utils/error-header.util';

@Injectable({
  providedIn: 'root',
})
export class DispatchRequestProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.inventoryApi + '/api/v1/dispatch-request';

  public getById(id: string, skipInterceptor = false): Observable<ApiResponse<DispatchRequest>> {
    const headers = getSkipperErrorHeader(skipInterceptor);
    return this.httpClient.get<ApiResponse<DispatchRequest>>(`${this.baseUrl}/${id}`, { headers });
  }

  public createForEntity(body: EntityKeyByReference): Observable<ApiResponse<DispatchRequest>> {
    return this.httpClient.post<ApiResponse<DispatchRequest>>(`${this.baseUrl}/entity-key`, body);
  }

  public search(
    command: DispatchRequestSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PaginatedApiResponse<DispatchRequest>>> {
    let queryParams = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (sortField) {
      queryParams += `&sortField=${encodeURIComponent(sortField)}&sortOrder=${sortOrder}`;
    }
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<DispatchRequest>>>(`${this.baseUrl}/search${queryParams}`, command);
  }

  public update(id: string, body: DispatchRequest): Observable<ApiResponse<DispatchRequest>> {
    return this.httpClient.put<ApiResponse<DispatchRequest>>(`${this.baseUrl}/${id}`, body);
  }

  /**
   * Actualiza solo el estado del DispatchRequest
   * @param id - ID del DispatchRequest
   * @param newStatus - Nuevo estado
   */
  public updateStatus(id: string, newStatus: number): Observable<ApiResponse<DispatchRequest>> {
    return this.httpClient.put<ApiResponse<DispatchRequest>>(`${this.baseUrl}/status/${id}`, newStatus);
  }

  /**
   * Descarga el reporte PDF del DispatchRequest
   * @param id - ID del DispatchRequest
   */
  public downloadReport(id: string): Observable<Blob> {
    return this.httpClient.get(`${this.baseUrl}/report/${id}`, {
      responseType: 'blob',
      params: { timezoneOffset: this.getLocalOffsetMinutes().toString() },
    });
  }

  /**
   * Returns the machine's UTC offset in minutes using the API convention
   * (UTC-6 => -360). JavaScript's `getTimezoneOffset()` returns the inverse,
   * so we negate it.
   */
  private getLocalOffsetMinutes(): number {
    return -new Date().getTimezoneOffset();
  }
}
